// Coding page interactive components

const { useState: codUS, useMemo: codUM } = React;

const CDT_CATALOG = [
  { code: 'D0120', name: 'Periodic oral evaluation - established patient', cat: 'Diagnostic', fee: '$62.00',
    desc: 'A recall exam for an established patient. Includes an oral cancer screening by the dentist and a targeted periodontal screening as indicated. Typically reimbursed twice per benefit year.',
    attachments: ['Established patient on file','Oral cancer screening recorded','Targeted perio screening (as needed)'],
    citations: [],
    tips: [
      'Mandatory oral cancer screening - record any findings in the chart.',
      'Use D0150 (not D0120) for comprehensive evals on new patients to avoid under-billing.',
      'For children under 3, use D0145 (counseling required) instead of D0120.',
      'D4910 (perio maintenance) does NOT include an exam - bill D0120 alongside when performed.',
    ],
    limitations: [
      'Usually capped at 2x per benefit year; some plans now limit to 1x annually.',
      'Cannot bill insured patients while offering the same service free to uninsured.',
      'Hygienists cannot diagnose from screenings unless permitted by state law.',
    ] },
  { code: 'D0150', name: 'Comprehensive oral evaluation - new or established patient', cat: 'Diagnostic', fee: '$98.00', locked: true,
    desc: 'Used by a general dentist and/or specialist when evaluating a patient comprehensively.',
    attachments: ['Radiographs (current)','Periodontal screening','Medical history review'],
    citations: [] },
  { code: 'D0274', name: 'Bitewing radiographs - four films', cat: 'Diagnostic', fee: '$78.00', locked: true,
    desc: 'Routine bitewings, four films, often paired with a recall exam.',
    attachments: ['Frequency last 12mo','Radiograph images'],
    citations: [] },
  { code: 'D1110', name: 'Prophylaxis - adult', cat: 'Preventive', fee: '$118.00', locked: true,
    desc: 'Adult prophylactic removal of plaque, calculus, and stains.',
    attachments: ['Periodontal status note'], citations: [] },
  { code: 'D1206', name: 'Topical application of fluoride varnish', cat: 'Preventive', fee: '$36.00', locked: true,
    desc: 'Fluoride varnish for caries prevention.',
    attachments: ['Caries risk assessment'], citations: [] },
  { code: 'D2740', name: 'Crown - porcelain/ceramic', cat: 'Restorative', fee: '$1,348.00', locked: true,
    desc: 'Single-unit ceramic crown.',
    attachments: ['Pre-op PA radiograph','Tooth/decay narrative','Restoration history'], citations: [] },
  { code: 'D4341', name: 'Periodontal scaling & root planing - 4+ teeth per quadrant', cat: 'Periodontics', fee: '$258.00', locked: true,
    desc: 'Therapeutic procedure for moderate-to-severe periodontitis. Quadrant-based.',
    attachments: ['Perio chart with depths','PA or FMX radiograph','Bone loss measurement','Severity narrative'], citations: [] },
  { code: 'D4342', name: 'Periodontal scaling & root planing - 1–3 teeth per quadrant', cat: 'Periodontics', fee: '$168.00', locked: true,
    desc: 'Same as D4341 but for limited teeth.',
    attachments: ['Perio chart','Specific tooth narrative'], citations: [] },
  { code: 'D4910', name: 'Periodontal maintenance', cat: 'Periodontics', fee: '$148.00', locked: true,
    desc: 'Follows active periodontal therapy. Three- to four-month intervals.',
    attachments: ['Prior SRP date','Updated perio chart'], citations: [] },
  { code: 'D7140', name: 'Extraction - erupted tooth or exposed root', cat: 'Oral Surgery', fee: '$185.00', locked: true,
    desc: 'Routine extraction of an erupted tooth, including local anesthesia.',
    attachments: ['Pre-op radiograph','Reason narrative'], citations: [] },
  { code: 'D9215', name: 'Local anesthesia in conjunction with operative or surgical procedures', cat: 'Adjunctive', fee: '$32.00', locked: true,
    desc: 'Local anesthesia, billed adjunctively. Payer-dependent.',
    attachments: ['Linked operative code','Drug + concentration'], citations: [] },
  { code: 'D9230', name: 'Inhalation of nitrous oxide / analgesia, anxiolysis', cat: 'Adjunctive', fee: '$58.00', locked: true,
    desc: 'Nitrous oxide for anxiety/analgesia, typically used during operative work.',
    attachments: ['Linked procedure','Time noted'], citations: [] },
];

function CdtLookup() {
  const [q, setQ] = codUS('');
  const [active, setActive] = codUS('D0120');

  const filtered = codUM(() => {
    if (!q) return CDT_CATALOG;
    const lc = q.toLowerCase();
    return CDT_CATALOG.filter(c =>
      c.code.toLowerCase().includes(lc) ||
      c.name.toLowerCase().includes(lc) ||
      c.cat.toLowerCase().includes(lc)
    );
  }, [q]);

  const detail = CDT_CATALOG.find(c => c.code === active) || CDT_CATALOG[0];

  return (
    <div className="lookup-shell">
      <div className="lookup-search">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder='Try "perio scaling", "crown", or "D2740"' />
        <span className="lookup-kbd">⌘K</span>
      </div>
      <div className="lookup-body">
        <div className="lookup-list" style={{position:'relative'}}>
          {filtered.map(c => (
            <div
              key={c.code}
              className={"lookup-item " + (c.code === active ? "active " : "") + (c.locked ? "locked" : "")}
              onClick={() => { if (!c.locked) setActive(c.code); }}
              style={c.locked ? {filter:'blur(4px)', userSelect:'none', pointerEvents:'none'} : {}}
            >
              <span className="lookup-item-code">{c.code}</span>
              <span className="lookup-item-name">{c.name}</span>
              <span className="lookup-item-fee">{c.fee}</span>
            </div>
          ))}
          {filtered.some(c => c.locked) && (
            <div style={{
              position:'absolute',
              left:0, right:0,
              top:'30%', bottom:0,
              background:'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 35%, #fff 100%)',
              display:'flex', alignItems:'center', justifyContent:'center',
              padding:'24px',
              pointerEvents:'none',
            }}>
              <div style={{
                background:'#fff',
                border:'1px solid var(--border)',
                borderRadius:'var(--r-lg)',
                padding:'18px 22px',
                boxShadow:'0 20px 50px -10px rgba(15,23,42,0.18)',
                textAlign:'center',
                maxWidth:320,
                pointerEvents:'auto',
              }}>
                <div style={{fontSize:11, fontWeight:700, letterSpacing:'0.08em', color:'var(--teal-deep)', textTransform:'uppercase', marginBottom:8}}>
                  🔒 1,000+ more codes
                </div>
                <div style={{fontSize:14, color:'var(--text)', fontWeight:600, marginBottom:6, lineHeight:1.35}}>
                  Unlock the full code library
                </div>
                <div style={{fontSize:12.5, color:'var(--text-soft)', lineHeight:1.5, marginBottom:14}}>
                  Search every CDT code with payer rules, attachment checklists, and AI guidance - free for 14 days.
                </div>
                <a href="https://app.denticode.com/#/sign-up" className="btn btn-primary btn-sm" style={{fontSize:13}}>Start free trial →</a>
              </div>
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{padding:24, color:'var(--text-soft)', fontSize:13, textAlign:'center'}}>
              No matches. Denticode can still bill a custom code - ask the AI.
            </div>
          )}
        </div>
        <div className="lookup-detail">
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <div>
              <span className="hd-cdt-code" style={{fontSize:14}}>{detail.code}</span>
              <span className="chip" style={{marginLeft:10, fontSize:11}}>{detail.cat}</span>
            </div>
            <span style={{fontFamily:'var(--font-mono)', fontSize:18, fontWeight:600}}>{detail.fee}</span>
          </div>
          <h3>{detail.name}</h3>
          <div style={{fontSize:14, color:'var(--text-muted)', lineHeight:1.55}}>{detail.desc}</div>

          <div className="lookup-detail-row">
            <div className="lookup-detail-label">Required</div>
            <div className="lookup-detail-val">
              <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                {detail.attachments.map((a,i) => (
                  <span key={i} className="chip chip-teal" style={{fontSize:11, height:24}}>{a}</span>
                ))}
              </div>
            </div>
          </div>

          {detail.tips && (
            <div className="lookup-detail-row">
              <div className="lookup-detail-label">Tips</div>
              <div className="lookup-detail-val">
                <ul style={{margin:0, paddingLeft:18, display:'flex', flexDirection:'column', gap:6, fontSize:13.5, lineHeight:1.5}}>
                  {detail.tips.map((t,i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            </div>
          )}

          {detail.limitations && (
            <div className="lookup-detail-row">
              <div className="lookup-detail-label">Limitations</div>
              <div className="lookup-detail-val">
                <ul style={{margin:0, paddingLeft:18, display:'flex', flexDirection:'column', gap:6, fontSize:13.5, lineHeight:1.5}}>
                  {detail.limitations.map((t,i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            </div>
          )}

          <div className="lookup-detail-row">
            <div className="lookup-detail-label">AI guidance</div>
            <div className="lookup-detail-val">
              Denticode infers D0120 when the transcript signals a routine recall on an established patient, oral cancer screening is documented, and no comprehensive (D0150) workup is performed. If the patient is new or the exam is comprehensive, D0150 is suggested instead.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Claim flow ----
function ClaimFlow() {
  return (
    <div className="claim-flow">
      <div className="flow-step">
        <span className="flow-step-num">01</span>
        <div className="flow-step-title">Note finalized</div>
        <div className="flow-step-body">Provider hits finalize. Denticode extracts procedures, diagnoses, and provider info.</div>
        <div className="flow-step-art">
          <div className="flow-mini">{`procedures: ["D4341","D9215"]\ndiagnoses: ["periodontitis,UR"]`}</div>
        </div>
      </div>
      <div className="flow-step">
        <span className="flow-step-num">02</span>
        <div className="flow-step-title">Codes inferred</div>
        <div className="flow-step-body">AI suggests CDT codes with citations, ranked by confidence. Clinician approves.</div>
        <div className="flow-step-art">
          <div className="flow-mini" style={{display:'flex', flexDirection:'column', gap:6}}>
            <div>D4341  ✓ approved</div>
            <div>D9215  ✓ approved</div>
            <div>D1206  ○ skipped</div>
          </div>
        </div>
      </div>
      <div className="flow-step">
        <span className="flow-step-num">03</span>
        <div className="flow-step-title">Attachments checked</div>
        <div className="flow-step-body">For each code, payer-specific attachment rules ensure nothing is missing.</div>
        <div className="flow-step-art">
          <div className="flow-mini" style={{display:'flex', flexDirection:'column', gap:6}}>
            <div>✓ PA radiograph</div>
            <div>✓ Perio chart</div>
            <div>✓ Narrative drafted</div>
          </div>
        </div>
      </div>
      <div className="flow-step">
        <span className="flow-step-num">04</span>
        <div className="flow-step-title">Claim shipped</div>
        <div className="flow-step-body">Fees pulled from payer schedule. Submitted to clearing house or written to PMS.</div>
        <div className="flow-step-art">
          <div className="flow-mini" style={{color:'#6BD7E8'}}>
            → Sent · Aetna PPO<br/>
            $368.00 · 12s
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Fees table ----
function FeesTable() {
  const ROWS = [
    { code:'D0150', name:'Comprehensive oral evaluation', ucr:'$120', aetna:'$98', delta:'$92', cigna:'$105' },
    { code:'D0274', name:'Bitewings - four films', ucr:'$95', aetna:'$78', delta:'$72', cigna:'$80', drift:true },
    { code:'D1110', name:'Prophylaxis - adult', ucr:'$140', aetna:'$118', delta:'$110', cigna:'$122' },
    { code:'D2740', name:'Crown - porcelain/ceramic', ucr:'$1,500', aetna:'$1,348', delta:'$1,260', cigna:'$1,380' },
    { code:'D4341', name:'Periodontal SRP - 4+ teeth', ucr:'$310', aetna:'$258', delta:'$248', cigna:'$270', drift:true },
    { code:'D4910', name:'Periodontal maintenance', ucr:'$180', aetna:'$148', delta:'$138', cigna:'$152' },
    { code:'D7140', name:'Extraction - erupted', ucr:'$220', aetna:'$185', delta:'$172', cigna:'$192' },
    { code:'D9215', name:'Local anesthesia', ucr:'$38', aetna:'$32', delta:'$28', cigna:'$34' },
  ];
  return (
    <div className="fees-table">
      <div className="fees-header">
        <span>Code</span><span>Procedure</span>
        <span>UCR</span><span className="col-hide">Aetna PPO</span><span className="col-hide">Delta Premier</span><span className="col-hide">Cigna DPPO</span>
      </div>
      {ROWS.map(r => (
        <div className="fees-row" key={r.code}>
          <span className="code">{r.code}</span>
          <span>{r.name}{r.drift && <span className="fee-drift">⚠ drift</span>}</span>
          <span className="fee">{r.ucr}</span>
          <span className="fee col-hide">{r.aetna}</span>
          <span className="fee col-hide">{r.delta}</span>
          <span className="fee col-hide">{r.cigna}</span>
        </div>
      ))}
    </div>
  );
}

function mountIfExists(id, el) {
  const node = document.getElementById(id);
  if (node) ReactDOM.createRoot(node).render(el);
}
mountIfExists('lookup-mount', <CdtLookup />);
mountIfExists('claim-mount', <ClaimFlow />);
mountIfExists('fees-mount', <FeesTable />);
